import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
} from '@mui/material';
import { FC, useRef, useState } from 'react';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { useMaxSizeForViewport } from '../useWindowDimensions';
import { CompareControlButtons } from '../CompareControls/CompareControlButtons';
import { SyncControlsButton } from '../SyncedControls/SyncControlsButton';
import { MaterialControls } from '../MaterialControls/MaterialControls';
import { ModelTypeControls } from '../LayerControls/TypeControls';
import { LayerToggles } from '../LayerControls/LayerToggles';
import { CameraShortcuts } from './CameraShortcuts';
import { DndContext, useDraggable } from '@dnd-kit/core';
import { Coordinates } from '@dnd-kit/core/dist/types';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';

const defaultCoordinates = {
  x: 12,
  y: 12,
};

export const CanvasControls: FC = () => {
  const [{ x, y }, setCoordinates] = useState<Coordinates>(defaultCoordinates);
  return (
    <DndContext
      modifiers={[restrictToWindowEdges]}
      autoScroll={false}
      onDragEnd={({ delta }) => {
        setCoordinates(({ x, y }) => {
          return {
            x: x + delta.x,
            y: y + delta.y,
          };
        });
      }}
    >
      <DraggableAccordion x={x} y={y} />
    </DndContext>
  );
};

interface DraggableAccordionProps {
  x: number;
  y: number;
}

const DraggableAccordion: FC<DraggableAccordionProps> = ({ x, y }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useMaxSizeForViewport(containerRef, 36);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable',
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      style={{
        position: 'absolute',
        top: y,
        left: x,
        zIndex: 1000,
      }}
    >
      <Accordion
        ref={setNodeRef}
        style={{ width: 250, borderRadius: '12px', ...style }}
      >
        <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              height: '100%',
            }}
          >
            <IconButton
              {...listeners}
              {...attributes}
              size='small'
              sx={{
                cursor: 'move',
              }}
            >
              <DragIndicatorIcon />
            </IconButton>
            <Typography component='span'>Controls</Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails
          ref={containerRef}
          style={{
            overflow: 'auto',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <CompareControlButtons />
            <SyncControlsButton />
            <ModelTypeControls />
            <MaterialControls />
            <CameraShortcuts />
            <LayerToggles />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
